/**
 * Appointment files service — calls the ECS API server.
 *
 * Backed by `GET /appointments/{appointment_id}/files` and
 * `POST /appointments/files/batch`, which resolve an appointment's files
 * server-side over its `originalAppointmentID` family (reschedule chain =
 * writeable, siblings = read-only) and return the family alongside.
 *
 * Wave 0 of the Portal cutover (`specs/appointment-file-reads-portal-cutover.md`):
 * the client, the `FileDisplayData` mapper, the coalescing loader and the
 * shadow-compare reporter. Each later wave points one group of readers here
 * behind its own flag.
 */

import {
  AppointmentFileReferenceStatus,
  FileProvider,
  FileType,
} from "~/api/API";
import { createApiClient } from "~/api/apiClient";
import type { components } from "~/api/generated/api-server-types";
import type { FileDisplayData } from "~/api/queries/useAppointmentFiles";
import { errorReporter, toError } from "~/error-handling";
import { chunkIds, settleWithLimit } from "~/services/batchChunks";
import { createBatchLoader } from "~/services/batchLoader";

export type AppointmentFilesResult =
  components["schemas"]["GetAppointmentFilesResponse"];
export type AppointmentFile = components["schemas"]["AppointmentFile"];
export type AppointmentFamilyMember =
  components["schemas"]["AppointmentFamilyMember"];
export type AppointmentFilesBatchEntry =
  components["schemas"]["AppointmentFilesBatchEntry"];

const ENDPOINT = "/appointments/{appointment_id}/files";
const REPORT_TAGS = { service: "api-server", endpoint: ENDPOINT } as const;

const BATCH_ENDPOINT = "/appointments/files/batch";
const BATCH_REPORT_TAGS = {
  service: "api-server",
  endpoint: BATCH_ENDPOINT,
} as const;

/**
 * Ids per batch request — half the endpoint's 100-id cap (`_MAX_BATCH` in
 * `ecs/api_server/routers/appointment_files.py`). A 99-id batch measured ~2.5s
 * from a laptop, but ops-todos measured comfortably at 100 too and then 504'd
 * at p99 under real load (`MAX_BATCH_IDS` in `opsTodos.ts`). 50 keeps a
 * 100-row dash page at two requests with headroom under API Gateway's 30s.
 *
 * Exported so tests derive their chunk arithmetic from the real value.
 */
export const MAX_BATCH_IDS = 50;

/** Chunk requests in flight at once — a 100-row page is exactly one window. */
const MAX_CONCURRENT_CHUNKS = 2;

/**
 * Fetch one appointment's resolved files and family. Resolves `null` on 404
 * (the appointment no longer exists — e.g. a refetch after delete), which
 * callers render as no files.
 *
 * Sentry reporting follows `fetchOpsTodos`: HTTP error statuses are reported by
 * the apiClient middleware (403/404 at warning), so only request rejects and
 * empty 200 bodies are reported here.
 */
export async function fetchAppointmentFiles(
  appointmentId: string,
): Promise<AppointmentFilesResult | null> {
  const client = createApiClient();

  const { data, error, response } = await client
    .GET(ENDPOINT, { params: { path: { appointment_id: appointmentId } } })
    .catch((cause: unknown): never => {
      const errObj = toError(cause, "Failed to fetch appointment files");
      errorReporter.reportApplicationError(errObj, {
        tags: REPORT_TAGS,
        extra: { appointmentId },
      });
      throw errObj;
    });

  if (response.status === 404) return null;

  // Already reported by the apiClient middleware — don't double-report.
  if (error) {
    throw new Error(
      `Failed to fetch appointment files: ${JSON.stringify(error)}`,
    );
  }

  if (!data) {
    const errObj = new Error(
      "Failed to fetch appointment files: empty response body",
    );
    errorReporter.reportApplicationError(errObj, {
      tags: REPORT_TAGS,
      extra: { appointmentId },
    });
    throw errObj;
  }

  return data;
}

/**
 * Fetch many appointments' files in one call (no `family`). Chunks at
 * `MAX_BATCH_IDS`; chunks fail independently and the result is the flat union
 * of every fulfilled chunk's entries. Rejects only when every chunk failed, so
 * a total outage surfaces as a query error — same contract as
 * `fetchOpsTodosBatch`.
 */
export async function fetchAppointmentFilesBatch(
  appointmentIds: string[],
): Promise<AppointmentFilesBatchEntry[]> {
  if (appointmentIds.length === 0) return [];

  const client = createApiClient();
  const settled = await settleWithLimit(
    chunkIds(appointmentIds, MAX_BATCH_IDS).map(
      (ids) => () => fetchAppointmentFilesBatchChunk(client, ids),
    ),
    MAX_CONCURRENT_CHUNKS,
  );

  const fulfilled = settled.filter(
    (r): r is PromiseFulfilledResult<AppointmentFilesBatchEntry[]> =>
      r.status === "fulfilled",
  );
  if (fulfilled.length === 0) {
    throw (settled[0] as PromiseRejectedResult).reason;
  }
  return fulfilled.flatMap((r) => r.value);
}

/** See `COALESCE_WINDOW_MS` in `opsTodos.ts`. */
const COALESCE_WINDOW_MS = 20;

/**
 * Per-appointment loader shared by every batch reader, so N rows mounting in
 * one commit become one request. Outcomes per id:
 *   - `files`               → resolve them;
 *   - `not_found`           → resolve `[]` (deleted appointment: no files);
 *   - `forbidden` / `error` → resolve `null`. The server answered, so this is
 *     data, not a failed request — the caller decides (fall back, or show
 *     nothing). Rejecting would make react-query retry it (see `opsTodosLoader`);
 *   - no entry              → reject; that id's chunk failed and a retry is right.
 */
const appointmentFilesLoader = createBatchLoader<AppointmentFile[] | null>({
  windowMs: COALESCE_WINDOW_MS,
  fetchBatch: async (appointmentIds) => {
    const entries = await fetchAppointmentFilesBatch(appointmentIds);
    return new Map(
      entries.map((entry) => [
        entry.appointment_id,
        entry.error === "not_found" ? [] : (entry.files ?? null),
      ]),
    );
  },
  missingKeyError: (appointmentId) =>
    new Error(
      `Failed to fetch appointment files for ${appointmentId}: no entry returned`,
    ),
});

/**
 * One appointment's files, batched with every other id requested in the same
 * ~20ms window into a single `POST /appointments/files/batch`.
 */
export function loadAppointmentFiles(
  appointmentId: string,
): Promise<AppointmentFile[] | null> {
  return appointmentFilesLoader.load(appointmentId);
}

async function fetchAppointmentFilesBatchChunk(
  client: ReturnType<typeof createApiClient>,
  appointmentIds: string[],
): Promise<AppointmentFilesBatchEntry[]> {
  const { data, error } = await client
    .POST(BATCH_ENDPOINT, { body: { appointment_ids: appointmentIds } })
    .catch((cause: unknown): never => {
      const errObj = toError(
        cause,
        "Failed to fetch appointment files (batch)",
      );
      errorReporter.reportApplicationError(errObj, {
        tags: BATCH_REPORT_TAGS,
        extra: { appointmentIdCount: appointmentIds.length },
      });
      throw errObj;
    });

  // Already reported by the apiClient middleware — don't double-report.
  if (error) {
    throw new Error(
      `Failed to fetch appointment files (batch): ${JSON.stringify(error)}`,
    );
  }

  if (!data) {
    const errObj = new Error(
      "Failed to fetch appointment files (batch): empty response body",
    );
    errorReporter.reportApplicationError(errObj, {
      tags: BATCH_REPORT_TAGS,
      extra: { appointmentIdCount: appointmentIds.length },
    });
    throw errObj;
  }

  return data.entries;
}

// ============================================================================
// Mapper — API file → FileDisplayData
// ============================================================================

const FILE_TYPES: ReadonlySet<string> = new Set(Object.values(FileType));
const FILE_PROVIDERS: ReadonlySet<string> = new Set(
  Object.values(FileProvider),
);

function isFileType(value: string): value is FileType {
  return FILE_TYPES.has(value);
}

function isFileProvider(value: string): value is FileProvider {
  return FILE_PROVIDERS.has(value);
}

/**
 * The status the Portal's readers expect, relative to `appointmentId`. The
 * server stamps every reschedule-chain file `ORIGINAL`; the junction reserved
 * that for files uploaded *here* and called the rest of the chain
 * `INHERITED_WRITEABLE` — and `UploadTab` reads `ORIGINAL` as "uploaded here".
 */
function referenceStatusFor(
  file: AppointmentFile,
  appointmentId: string,
): AppointmentFileReferenceStatus {
  if (file.source_appointment_id === appointmentId) {
    return AppointmentFileReferenceStatus.ORIGINAL;
  }
  return file.is_read_only
    ? AppointmentFileReferenceStatus.INHERITED_READ_ONLY
    : AppointmentFileReferenceStatus.INHERITED_WRITEABLE;
}

/**
 * Map the endpoint's files to the `FileDisplayData[]` every reader caches at
 * `appointmentFilesKeys.detail(appointmentId)`. Drops key-less rows, as
 * `appointmentFilesQueryConfig` does. `appointmentID` is the server-computed
 * owner (`resolveFileOwnership` semantics).
 */
export function toFileDisplayData(
  appointmentId: string,
  files: AppointmentFile[],
): FileDisplayData[] {
  const mapped: FileDisplayData[] = [];
  for (const file of files) {
    if (file.key === null) continue;
    mapped.push({
      id: file.id,
      fileName: file.file_name ?? file.key.split("/").at(-1) ?? "",
      fileType:
        file.file_type && isFileType(file.file_type)
          ? file.file_type
          : FileType.UNDETERMINED,
      providedBy:
        file.provider && isFileProvider(file.provider)
          ? file.provider
          : FileProvider.UNSPECIFIED,
      uploaded: {
        dateTime: new Date(file.uploaded_at ?? new Date().toISOString()),
        user: file.uploaded_by ?? "",
      },
      key: file.key,
      fileSizeBytes: file.file_size_bytes,
      actions: file.key,
      appointmentID: file.owner_appointment_id,
      isReadOnly: file.is_read_only,
      referenceStatus: referenceStatusFor(file, appointmentId),
    });
  }
  return mapped;
}

// ============================================================================
// Shadow compare — legacy read vs API read, during each wave's soak
// ============================================================================

export interface FileSetDivergence {
  missingInApi: string[];
  extraInApi: string[];
  /** File id → the fields whose values differ. */
  changed: Record<string, string[]>;
}

/**
 * Diff two resolved sets by file id over the fields readers act on. Returns
 * `null` when they agree. Ids and field names only — never file names (PHI).
 */
export function diffFileSets(
  legacy: FileDisplayData[],
  api: FileDisplayData[],
): FileSetDivergence | null {
  const apiById = new Map(api.map((f) => [f.id, f]));
  const legacyIds = new Set(legacy.map((f) => f.id));

  const missingInApi: string[] = [];
  const changed: Record<string, string[]> = {};
  for (const file of legacy) {
    const other = apiById.get(file.id);
    if (!other) {
      missingInApi.push(file.id);
      continue;
    }
    const fields: string[] = [];
    if (file.referenceStatus !== other.referenceStatus) {
      fields.push("referenceStatus");
    }
    if (file.isReadOnly !== other.isReadOnly) fields.push("isReadOnly");
    if (file.appointmentID !== other.appointmentID) {
      fields.push("appointmentID");
    }
    if (file.fileType !== other.fileType) fields.push("fileType");
    if (fields.length > 0) changed[file.id] = fields;
  }
  const extraInApi = api.filter((f) => !legacyIds.has(f.id)).map((f) => f.id);

  if (
    missingInApi.length === 0 &&
    extraInApi.length === 0 &&
    Object.keys(changed).length === 0
  ) {
    return null;
  }
  return { missingInApi, extraInApi, changed };
}

const reportedDivergences = new Set<string>();

/**
 * Report a legacy-vs-API divergence to Sentry, at most once per
 * (surface, appointment) per session. `surface` names the wave's reader group
 * so each wave's soak can be read on its own.
 */
export function reportFileReadDivergence(
  surface: string,
  appointmentId: string,
  legacy: FileDisplayData[],
  api: FileDisplayData[],
): void {
  const reportKey = `${surface}:${appointmentId}`;
  if (reportedDivergences.has(reportKey)) return;

  const divergence = diffFileSets(legacy, api);
  if (!divergence) return;

  reportedDivergences.add(reportKey);
  errorReporter.reportMessage(
    "Appointment file read divergence (legacy vs api-server)",
    {
      level: "warning",
      tags: { service: "api-server", endpoint: ENDPOINT, surface },
      extra: { appointmentId, ...divergence },
    },
  );
}
